'use client'

import { useState } from 'react'
import { X, ExternalLink } from 'lucide-react'
import type { Task, Project, Sprint, Subtask, TaskStatus, TaskPriority } from '@/lib/types'
import { VALID_POINTS } from '@/lib/dev-sprint'
import { effectiveTaskStatus } from '@/lib/utils'
import { useToast } from '@/context/ToastContext'
import type { BoardData } from './types'
import { STATUS_META, PRIORITY_META } from './ui'

interface Props {
  task: Task
  project: Project | undefined
  team: BoardData['team']
  subtasks: Subtask[]
  sprints: Sprint[]
  canEdit: boolean
  isLead: boolean
  onClose: () => void
  onSaved: () => void
}

const STATUSES: TaskStatus[] = ['not-started', 'in-progress', 'done']
const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high']

export function TaskDrawer({ task, project, team, subtasks, sprints, canEdit, isLead, onClose, onSaved }: Props) {
  const { toast } = useToast()
  // Status EFEKTIF (kalau ada subtask non-rutin, status ditentukan subtask → auto/terkunci).
  const isAuto = subtasks.some((s) => !s.recurrence)
  const [title, setTitle] = useState(task.title)
  const [description, setDescription] = useState(task.description ?? '')
  const [status, setStatus] = useState<TaskStatus>(effectiveTaskStatus(task.status, subtasks))
  const [priority, setPriority] = useState<TaskPriority>(task.priority)
  const [assignee, setAssignee] = useState<string>(task.assignee ?? '')
  const [dueDate, setDueDate] = useState<string>((task.dueDate ?? '').slice(0, 10))
  const [points, setPoints] = useState<number | null>(task.points ?? null)
  const [sprintId, setSprintId] = useState<string>(task.sprintId ?? '')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    const body: Record<string, unknown> = {}
    if (canEdit) {
      if (!isAuto && status !== task.status) body.status = status
      if (dueDate !== (task.dueDate ?? '').slice(0, 10)) body.dueDate = dueDate || null
      if (points !== (task.points ?? null)) body.points = points
    }
    if (isLead) {
      if (title.trim() !== task.title) body.title = title.trim()
      if (description !== (task.description ?? '')) body.description = description
      if (priority !== task.priority) body.priority = priority
      if (assignee !== (task.assignee ?? '')) body.assignee = assignee || null
      if (sprintId !== (task.sprintId ?? '')) body.sprintId = sprintId || null
    }
    if (Object.keys(body).length === 0) { onClose(); return }
    try {
      const res = await fetch(`/api/dev/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Gagal menyimpan')
      toast('Task diperbarui', 'success')
      onSaved()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gagal menyimpan', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/25" onClick={onClose} />
      <div className="relative w-full max-w-[440px] h-full bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
        <div className="sticky top-0 bg-white border-b border-[#EDEBE5] px-5 py-3.5 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#AEADA8]">Detail Task</span>
          <button onClick={onClose} className="p-1 rounded-lg text-[#AEADA8] hover:text-[#1A1916] hover:bg-[#F2F1EC]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Project */}
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: project?.color ?? '#D4A843' }} />
            <span className="text-[12px] font-semibold text-[#6B6A65]">{project?.name ?? '—'}</span>
            {project && (
              <a href={`/projects/${project.id}`} target="_blank" rel="noreferrer" className="ml-auto text-[#AEADA8] hover:text-[#1A1916]" title="Buka project">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>

          {/* Title */}
          <Field label="Judul">
            {isLead ? (
              <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
            ) : (
              <p className="text-[15px] font-semibold text-[#1A1916]">{task.title}</p>
            )}
          </Field>

          {/* Status */}
          <Field label="Status">
            <div className="flex gap-1.5">
              {STATUSES.map((s) => {
                const active = status === s
                const m = STATUS_META[s]
                return (
                  <button
                    key={s}
                    disabled={!canEdit || isAuto}
                    onClick={() => setStatus(s)}
                    className="flex-1 px-2 py-1.5 rounded-lg text-[12px] font-semibold border transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    style={active ? { backgroundColor: m.bg, color: m.text, borderColor: m.dot } : { backgroundColor: '#fff', color: '#8B8A85', borderColor: '#E7E5DF' }}
                  >
                    {m.label}
                  </button>
                )
              })}
            </div>
            {isAuto && <p className="text-[11px] text-[#AEADA8] mt-1.5">Status otomatis dari subtask (centang subtask di halaman project).</p>}
          </Field>

          {/* Points */}
          <Field label="Story Points">
            <div className="flex gap-1.5">
              {VALID_POINTS.map((p) => {
                const active = points === p
                return (
                  <button
                    key={p}
                    disabled={!canEdit}
                    onClick={() => setPoints(active ? null : p)}
                    className={`w-9 h-9 rounded-lg text-[13px] font-bold border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${active ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-white text-[#6B6A65] border-[#E7E5DF] hover:border-[#C9C7C0]'}`}
                  >
                    {p}
                  </button>
                )
              })}
            </div>
          </Field>

          {/* Due date (menentukan minggu) */}
          <Field label="Deadline (menentukan minggu)">
            <input
              type="date"
              value={dueDate}
              disabled={!canEdit}
              onChange={(e) => setDueDate(e.target.value)}
              className={`${inputCls} disabled:opacity-60`}
            />
          </Field>

          {/* Assignee */}
          <Field label="Assignee">
            {isLead ? (
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className={inputCls}>
                <option value="">— belum di-assign —</option>
                {team.filter((m) => m.username).map((m) => (
                  <option key={m.email} value={m.username as string}>{m.name}</option>
                ))}
              </select>
            ) : (
              <p className="text-[13px] text-[#4A4844]">{task.assignee || '—'}</p>
            )}
          </Field>

          {/* Sprint */}
          <Field label="Sprint">
            {isLead ? (
              <select value={sprintId} onChange={(e) => setSprintId(e.target.value)} className={inputCls}>
                <option value="">— tanpa sprint —</option>
                {sprints.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            ) : (
              <p className="text-[13px] text-[#4A4844]">{sprints.find((s) => s.id === task.sprintId)?.name || '—'}</p>
            )}
          </Field>

          {/* Priority */}
          <Field label="Prioritas">
            {isLead ? (
              <div className="flex gap-1.5">
                {PRIORITIES.map((p) => {
                  const active = priority === p
                  const m = PRIORITY_META[p]
                  return (
                    <button
                      key={p}
                      onClick={() => setPriority(p)}
                      className="flex-1 px-2 py-1.5 rounded-lg text-[12px] font-semibold border transition-colors"
                      style={active ? { backgroundColor: `${m.color}18`, color: m.color, borderColor: m.color } : { backgroundColor: '#fff', color: '#8B8A85', borderColor: '#E7E5DF' }}
                    >
                      {m.label}
                    </button>
                  )
                })}
              </div>
            ) : (
              <p className="text-[13px] text-[#4A4844] capitalize">{task.priority}</p>
            )}
          </Field>

          {/* Description */}
          <Field label="Deskripsi">
            {isLead ? (
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className={`${inputCls} resize-y`} />
            ) : (
              <p className="text-[13px] text-[#6B6A65] whitespace-pre-wrap">{task.description || '—'}</p>
            )}
          </Field>
        </div>

        {(canEdit || isLead) && (
          <div className="sticky bottom-0 bg-white border-t border-[#EDEBE5] px-5 py-3 flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-xl text-[13px] font-medium text-[#6B6A65] hover:bg-[#F2F1EC]">Batal</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#1A1916] hover:bg-black disabled:opacity-60">
              {saving ? 'Menyimpan…' : 'Simpan'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const inputCls = 'w-full bg-white border border-[#E7E5DF] rounded-lg px-2.5 py-2 text-[13px] text-[#1A1916] focus:outline-none focus:ring-2 focus:ring-[#D4A843]/40'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#AEADA8] mb-1.5">{label}</label>
      {children}
    </div>
  )
}
